import {
  AddIcon,
  CheckIcon,
  InfoIcon,
  SettingsIcon,
} from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useReducedMotion } from 'framer-motion';
import { MotionBox } from '../../../components/motion/MotionComponents';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import FilterDrawer from '../../../components/filter-drawer/FilterDrawer';
import FilterStateIndicator from '../../../components/filter-state-indicator/FilterStateIndicator';
import PageLoader from '../../../components/layout/page-loader/PageLoader';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAuth } from '../../../auth/AuthContext';
import { getInsuranceList } from '../../../services/plans/listInsurance';
import { getMarketList } from '../../../services/markets/getMarketList';
import {
  readStatesDataCache,
  writeStatesDataCache,
} from '../../../utils/statesDataCache';
import { getCustomerIdFromToken } from '../../../utils/getCustomerIdFromToken';
import { Insurance } from '../../../types/plans/types';
import { State } from '../../../types/markets/types';

import { insuranceStatusOptions } from '../../../utils/insuranceStatusOptions';
import { isExternalMarket } from '../../../utils/externalEntity';
import { getStatesForMarketIds } from '../../../utils/getStatesForMarketIds';
import { stateOptions } from '../../../utils/stateOptions';
import StatePlansSection from './StatePlansSection';
import { plansFilterMappings } from './plansFilterMappings';

const FILTERS_STORAGE_KEY = 'plansFilters';

const PlansList: React.FC = () => {
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();

  const { canEditInsurance, isAdmin, permissions, isReady } = usePermissions();
  // isReady/permissions.isAdmin alone don't change when one customer
  // replaces another with the same readiness/admin values - loadStates and
  // loadAllInsurance below need customerId itself as a dependency so a
  // real customer switch is what actually triggers a reload, not just a
  // readiness or role change. useAuth's customerId is reactive (updates
  // via React state as the token is decoded), unlike the plain
  // getCustomerIdFromToken() snapshot used elsewhere in this file to stamp
  // individual in-flight requests.
  const { customerId } = useAuth();

  const [filters, setFilters] = useState<
    Record<string, string | string[]>
  >(() => {
    try {
      const saved = localStorage.getItem(FILTERS_STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });
  const [clearFiltersSignal, setClearFiltersSignal] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
    } catch (error) {
      console.error('Error saving filters to localStorage:', error);
    }
  }, [filters]);

  const [enhancedBadges, setEnhancedBadges] = useState(() => {
    try {
      const saved = localStorage.getItem('enhancedBadges');
      return saved ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  });
  const [isLegendOpen, setIsLegendOpen] = useState(false);

  const [statesLoading, setStatesLoading] = useState(true);
  const [statesData, setStatesData] = useState<State[]>([]);
  // A failed fetch with no usable cache must not look the same as "this
  // customer genuinely has no states/markets" - without this, statesData
  // just stays [], statesLoading clears, and the empty-state message
  // (with no retry) renders exactly as it would for a real empty customer.
  const [statesLoadError, setStatesLoadError] = useState(false);
  // True only when a cache hit let us render immediately but the live
  // refresh then failed - the data shown may be stale, as opposed to
  // statesLoadError (nothing to show at all).
  const [statesStale, setStatesStale] = useState(false);
  const [expandedStates, setExpandedStates] = useState<Set<string>>(
    new Set()
  );
  const [expandedManagePlans, setExpandedManagePlans] = useState<
    Set<string>
  >(new Set());
  const [sortOrder, setSortOrder] = useState<'az' | 'za'>('az');

  const [allInsurance, setAllInsurance] = useState<Insurance[]>([]);
  // allInsurance starting at [] is indistinguishable from "loaded, catalog
  // is genuinely empty" - without these, every state renders Manage Plans
  // (0) with an empty add-plan search while this is pending, and that wrong
  // state becomes permanent if the request fails.
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState(false);
  // Every StatePlansSection has its own mutation lock, so two different
  // sections' add/remove actions can finish close together and each call
  // this same onPlansChanged, overlapping two getInsuranceList requests. If
  // the older one resolves last, it would overwrite the newer catalog (or
  // set catalogError after a newer success already cleared it). Only the
  // response whose generation still matches the latest call is applied.
  const catalogRequestGenerationRef = useRef(0);

  // usePayorOptions() would fetch this exact same full insurance catalog a
  // second time (it has no awareness that loadAllInsurance below already
  // has it) - derive it from allInsurance instead of downloading it twice.
  const payorOptions = useMemo(() => {
    const uniqueNames = Array.from(
      new Set(
        allInsurance
          .map((insurance) => insurance.friendlyName)
          .filter((name): name is string => Boolean(name))
      )
    ).sort((a, b) => a.localeCompare(b));
    return uniqueNames.map((name) => ({ label: name, value: name }));
  }, [allInsurance]);

  const loadStates = useCallback(() => {
    if (!isReady) return;
    const adminFlag = permissions.isAdmin;
    // customerId (from useAuth, closed over here) is who was active when
    // THIS loadStates call started - correct for stamping the request.
    // isStaleCustomer must instead re-read the token live: this specific
    // closure keeps running even after a later customer change replaces
    // it with a new loadStates, so only an imperative, non-closed-over
    // lookup reflects who is ACTUALLY active by the time the response
    // arrives. Needed both for writeStatesDataCache and to gate applying
    // this response to React state at all below - discarding the cache
    // write alone still left customer A's response rendered for customer B.
    const requestCustomerId = customerId ?? '';
    const isStaleCustomer = () =>
      getCustomerIdFromToken() !== requestCustomerId;
    // A retry after a failed attempt starts with statesLoading already
    // false (cleared by the previous attempt's finally()) - without
    // resetting it here, the empty-state message renders for the whole
    // retry, making it look like a genuinely empty account mid-request.
    setStatesLoading(true);
    setStatesLoadError(false);

    const filterStates = (states: State[]): State[] => {
      if (adminFlag) return states;
      return states
        .map((state) => {
          const visibleMarkets = (state.markets || []).filter(
            (market) => !isExternalMarket(market)
          );
          return {
            ...state,
            markets: visibleMarkets,
            practiceCount: visibleMarkets.reduce(
              (count, market) => count + (market.practiceCount || 0),
              0
            ),
            providerCount: visibleMarkets.reduce(
              (count, market) => count + (market.providerCount || 0),
              0
            ),
            plansCount: visibleMarkets.reduce(
              (count, market) => count + (market.plansCount || 0),
              0
            ),
          };
        })
        .filter((state) => state.markets.length > 0);
    };

    const cached = readStatesDataCache<State>();
    let servedFromCache = false;
    if (cached) {
      setStatesData(filterStates(cached));
      setStatesLoading(false);
      servedFromCache = true;
    } else {
      setStatesData([]);
    }
    setStatesStale(false);

    getMarketList(undefined, undefined, undefined, adminFlag)
      .then(({ data }) => {
        if (isStaleCustomer()) return;
        if (data?.states) {
          writeStatesDataCache(data.states, requestCustomerId);
          setStatesData(filterStates(data.states));
        }
      })
      .catch((error) => {
        if (isStaleCustomer()) return;
        if (servedFromCache) setStatesStale(true);
        console.error('Failed to fetch market/state data:', error);
        setStatesLoadError(true);
      })
      .finally(() => {
        if (isStaleCustomer()) return;
        setStatesLoading(false);
      });
  }, [isReady, permissions.isAdmin, customerId]);

  useEffect(() => {
    loadStates();
  }, [loadStates]);

  const loadAllInsurance = useCallback(() => {
    if (!isReady) return;
    const requestGeneration = ++catalogRequestGenerationRef.current;
    const isStale = () =>
      catalogRequestGenerationRef.current !== requestGeneration;

    setCatalogLoading(true);
    setCatalogError(false);
    getInsuranceList()
      .then((res) => {
        if (isStale()) return;
        setAllInsurance(res.insurance || []);
      })
      .catch((error) => {
        if (isStale()) return;
        console.error('Failed to fetch insurance catalog:', error);
        setCatalogError(true);
      })
      .finally(() => {
        if (isStale()) return;
        setCatalogLoading(false);
      });
    // customerId isn't read in this body - it's listed purely so a customer
    // switch with unchanged isReady still recreates this callback and
    // triggers a reload (see the effect below). The generation counter
    // above guards ordering between overlapping requests of the same
    // customer; it's a separate concern from detecting the switch itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, customerId]);

  useEffect(() => {
    loadAllInsurance();
  }, [loadAllInsurance]);

  // Every plan is resolved to its assigned states via its marketIds (a plan
  // has no direct "states" field yet - see getStatesForMarketIds). statesData
  // must be a dependency here (not just allInsurance) - otherwise this can
  // compute against an empty states cache before loadStates() resolves and
  // never recompute once it does, leaving every state's assigned count at 0.
  const insuranceByState = useMemo(() => {
    const map = new Map<string, Insurance[]>();
    for (const insurance of allInsurance) {
      const states = getStatesForMarketIds(insurance.marketIds, statesData);
      for (const abbreviation of states) {
        const existing = map.get(abbreviation) || [];
        existing.push(insurance);
        map.set(abbreviation, existing);
      }
    }
    return map;
  }, [allInsurance, statesData]);

  const handleClearFilters = useCallback(() => {
    expandAllQueueRef.current = [];
    expandAllPendingNamesRef.current = new Set();
    everExpandedNamesRef.current = new Set();
    setExpandedStates(new Set());
    setFilters({});
    setClearFiltersSignal((prev) => !prev);
    localStorage.removeItem(FILTERS_STORAGE_KEY);
  }, []);

  const handleEnhancedBadgesToggle = useCallback(() => {
    setEnhancedBadges((prev: boolean) => {
      const newValue = !prev;
      localStorage.setItem('enhancedBadges', JSON.stringify(newValue));
      return newValue;
    });
  }, []);

  const handleApplyFilters = useCallback(
    (newFilters: Record<string, string[] | string>) => {
      const formattedFilters: Record<string, string | string[]> = {};
      Object.entries(newFilters).forEach(([uiKey, value]) => {
        if (
          value &&
          value !== '' &&
          (Array.isArray(value) ? value.length > 0 : true)
        ) {
          const apiKey = plansFilterMappings[uiKey] || uiKey;
          formattedFilters[apiKey] = value;
        }
      });
      expandAllQueueRef.current = [];
      expandAllPendingNamesRef.current = new Set();
      everExpandedNamesRef.current = new Set();
      setExpandedStates(new Set());
      setFilters(formattedFilters);
    },
    []
  );

  // "Expand all" fans out to one usePagination-driven request per state.
  // Expanding everything in a single tick would fire one request per state
  // simultaneously for every mounted grid. A fixed delay between batches
  // doesn't actually cap concurrency - on a slow endpoint, earlier batches'
  // requests are still in flight when later ones start, so the in-flight
  // count keeps growing regardless. Instead, release a fixed number of
  // states at once and only release the next one when a released state's
  // own request actually finishes (reported via onSectionLoadSettled,
  // wired to each StatePlansSection below) - a true concurrency cap.
  const EXPAND_ALL_CONCURRENCY = 5;
  const expandAllQueueRef = useRef<string[]>([]);
  const expandAllPendingNamesRef = useRef<Set<string>>(new Set());
  // usePagination skips re-fetching when re-enabled with filters that
  // match its last successful fetch - true for every section already
  // expanded right now, AND for one expanded earlier then collapsed (the
  // section stays mounted; its usePagination instance - and its dedup ref -
  // persists across collapse/re-expand). Releasing one of those into the
  // queue would never see a loading edge, permanently stranding that slot.
  // A section's very first expansion is always guaranteed to fetch (its
  // dedup ref starts empty, which can never match a real filters string),
  // so tracking "ever expanded" tells us exactly which ones are safe to
  // throttle. This classification goes stale the moment filters change,
  // though: usePagination compares against the NEW filters and legitimately
  // starts a fresh request for every section that's still expanded, or
  // that this tracking would otherwise wave through as a no-op - bypassing
  // the cap again, one request per state. The effect below resets this
  // tracking (and collapses everything) whenever filters change, so the
  // next expansion - individual or "Expand all" - is always forced back
  // through the throttled queue instead of firing uncapped.
  const everExpandedNamesRef = useRef<Set<string>>(new Set());

  const releaseNextQueuedExpand = useCallback(() => {
    const next = expandAllQueueRef.current.shift();
    if (next === undefined) return;
    expandAllPendingNamesRef.current.add(next);
    everExpandedNamesRef.current.add(next);
    setExpandedStates((prev) => {
      if (prev.has(next)) return prev;
      const nextSet = new Set(prev);
      nextSet.add(next);
      return nextSet;
    });
  }, []);

  const handleSectionLoadSettled = useCallback(
    (stateName: string) => {
      // Only a state this queue actually released should consume a slot -
      // a state expanded individually (not via "Expand all") finishing its
      // own load must not also pull the next queued state in.
      if (!expandAllPendingNamesRef.current.has(stateName)) return;
      expandAllPendingNamesRef.current.delete(stateName);
      releaseNextQueuedExpand();
    },
    [releaseNextQueuedExpand]
  );

  const clearExpandAllQueue = useCallback(() => {
    expandAllQueueRef.current = [];
    expandAllPendingNamesRef.current = new Set();
  }, []);

  useEffect(() => clearExpandAllQueue, [clearExpandAllQueue]);

  // A changed filter invalidates both any in-progress "Expand all" queue
  // and the "already fetched" tracking those decisions rely on - without
  // this, a filter change while sections are expanded (or were expanded
  // earlier) fans out one uncapped request per state again, exactly the
  // concurrency cap this queue exists to prevent. Collapsing everything
  // and clearing the tracking means the next expansion always goes
  // through releaseNextQueuedExpand, never around it.
  useEffect(() => {
    clearExpandAllQueue();
    if (everExpandedNamesRef.current.size > 0) {
      everExpandedNamesRef.current = new Set();
    }
    setExpandedStates((prev) => (prev.size > 0 ? new Set() : prev));
  }, [filters, clearExpandAllQueue]);

  const toggleExpand = (stateName: string) => {
    clearExpandAllQueue();
    setExpandedStates((prev) => {
      const next = new Set(prev);
      if (next.has(stateName)) {
        next.delete(stateName);
      } else {
        next.add(stateName);
        everExpandedNamesRef.current.add(stateName);
      }
      return next;
    });
  };

  const toggleManage = (stateName: string) => {
    setExpandedManagePlans((prev) => {
      const next = new Set(prev);
      if (next.has(stateName)) {
        next.delete(stateName);
      } else {
        next.add(stateName);
      }
      return next;
    });
  };

  const toggleAllExpand = () => {
    clearExpandAllQueue();
    if (allVisibleExpanded) {
      setExpandedStates(new Set());
      return;
    }
    const names = sortedStates.map((s) => s.name);
    const alreadyFetchedNames = names.filter((name) =>
      everExpandedNamesRef.current.has(name)
    );
    const freshNames = names.filter(
      (name) => !everExpandedNamesRef.current.has(name)
    );

    // These have fetched before (currently expanded, or expanded earlier
    // and since collapsed) - usePagination will skip re-fetching them, so
    // there's no request to throttle; expand them immediately rather than
    // routing them through the concurrency-limited queue.
    if (alreadyFetchedNames.length > 0) {
      setExpandedStates((prev) => {
        const nextSet = new Set(prev);
        alreadyFetchedNames.forEach((name) => nextSet.add(name));
        return nextSet;
      });
    }

    expandAllQueueRef.current = freshNames;
    for (let i = 0; i < EXPAND_ALL_CONCURRENCY; i++) {
      releaseNextQueuedExpand();
    }
  };

  // Only market-based marketIds/filters remain here; the state filter is
  // applied per-section by StatePlansSection, so it's excluded from the
  // filters forwarded into each section to avoid double-filtering.
  const globalFiltersForSections = useMemo(() => {
    const rest = { ...filters };
    delete rest.state;
    return rest;
  }, [filters]);

  const stateAbbreviationByName = useMemo(() => {
    const map = new Map<string, string>();
    for (const option of stateOptions) {
      map.set(option.label.toLowerCase(), option.value);
    }
    return map;
  }, []);

  // The state filter is stripped from globalFiltersForSections (each section
  // applies its own state internally), but that means it must be applied
  // here instead, against which state CARDS render at all - otherwise
  // choosing States in the filter drawer has no visible effect.
  const filteredStatesData = useMemo(() => {
    const selected = filters.state;
    const selectedList = Array.isArray(selected)
      ? selected
      : selected
      ? [selected]
      : [];
    if (selectedList.length === 0) return statesData;

    const selectedSet = new Set(selectedList);
    return statesData.filter((state) => {
      const abbreviation = stateAbbreviationByName.get(state.name.toLowerCase());
      return abbreviation ? selectedSet.has(abbreviation) : false;
    });
  }, [statesData, filters.state, stateAbbreviationByName]);

  const sortedStates = useMemo(() => {
    return [...filteredStatesData].sort((a, b) =>
      sortOrder === 'az'
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name)
    );
  }, [filteredStatesData, sortOrder]);

  // A size comparison alone can't tell whether the CURRENTLY VISIBLE states
  // are the ones expanded - if the state filter changes, expandedStates can
  // still hold hidden state names whose count happens to match
  // sortedStates.length, which would misreport "all expanded" for a set of
  // states that are actually all collapsed. toggleAllExpand (defined above)
  // only reads this inside its onClick body, so it safely closes over this
  // even though it's declared afterward - it's just not safe to reference
  // from plain top-level render code before this point.
  const allVisibleExpanded =
    sortedStates.length > 0 &&
    sortedStates.every((state) => expandedStates.has(state.name));

  return (
    <>
      <Helmet>
        <title>Insurance Plans | CHS | Resource Matrix — Denim Health</title>
      </Helmet>
      <MotionBox
        ml={{ base: '1rem', md: '2rem', lg: '11.25rem' }}
        mr={{ base: '1rem', md: '2rem', lg: '11.25rem' }}
        mb="3rem"
        mt="1.75rem"
        initial={
          prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 6 }
        }
        animate={{ opacity: 1, y: 0 }}
      >
        <Box>
          <Flex
            justifyContent={'space-between'}
            alignItems={'center'}
            mb="1.06rem"
            wrap={{ base: 'wrap', lg: 'nowrap' }}
            gap={{ base: '1rem', lg: '0' }}
          >
            <Text
              fontSize={'1.25rem'}
              fontWeight={'700'}
              lineHeight={'120%'}
              color="text.secondary"
            >
              All Plans
            </Text>
            <Flex gap="0.75rem" alignItems="center" flexWrap="wrap">
              <Button
                variant="outline"
                borderColor="gray.200"
                bg="white"
                color="text.primary"
                size="md"
                h="2.5rem"
                borderRadius="0.375rem"
                onClick={toggleAllExpand}
              >
                {allVisibleExpanded
                  ? 'Collapse all'
                  : 'Expand all'}
              </Button>

              {(canEditInsurance() || isAdmin()) && (
                <Button
                  colorScheme="blue"
                  background="blue.500"
                  color="white"
                  leftIcon={<AddIcon />}
                  size="md"
                  h="2.5rem"
                  px="1.5rem"
                  py="0.625rem"
                  fontSize="0.875rem"
                  fontWeight="600"
                  borderRadius="0.375rem"
                  minW="fit-content"
                  _hover={{
                    bg: 'blue.600',
                    transform: 'translateY(-1px)',
                    boxShadow: 'md',
                  }}
                  _active={{
                    bg: 'blue.700',
                    transform: 'translateY(0)',
                  }}
                  transition="all 0.2s ease-in-out"
                  onClick={() => navigate('/plans/create')}
                >
                  Add Insurance Plan
                </Button>
              )}

              <Menu>
                <MenuButton
                  as={IconButton}
                  icon={<SettingsIcon />}
                  size="sm"
                  aria-label="Settings"
                  borderRadius="0.375rem"
                  background="background.secondary"
                  width="2.5rem"
                  height="2.5rem"
                  cursor="pointer"
                  border="none"
                  transition="all 0.2s ease-in-out"
                  _hover={{
                    background: 'gray.100',
                  }}
                />
                <MenuList zIndex={1000}>
                  <MenuItem
                    onClick={handleEnhancedBadgesToggle}
                    color={enhancedBadges ? 'blue.600' : 'gray.700'}
                    fontWeight={enhancedBadges ? '600' : '400'}
                    fontSize="0.875rem"
                    icon={enhancedBadges ? <CheckIcon /> : undefined}
                  >
                    Enhanced Badges {enhancedBadges ? 'ON' : 'OFF'}
                  </MenuItem>
                  <MenuItem
                    onClick={() => setIsLegendOpen(true)}
                    icon={<InfoIcon />}
                    color="blue.600"
                  >
                    Status Legend & Help
                  </MenuItem>
                </MenuList>
              </Menu>

              <FilterDrawer
                filterTitle="Insurance Plans"
                filterOptions={{
                  States: stateOptions,
                  Status: insuranceStatusOptions,
                  Payor: payorOptions,
                }}
                onApplyFilters={handleApplyFilters}
                clearFilters={clearFiltersSignal}
                initialFilters={(() => {
                  const uiFilters: Record<string, string | string[]> = {};
                  Object.entries(filters).forEach(([apiKey, value]) => {
                    const uiKey =
                      Object.keys(plansFilterMappings).find(
                        (key) => plansFilterMappings[key] === apiKey
                      ) || apiKey;
                    uiFilters[uiKey] = value;
                  });
                  return uiFilters;
                })()}
              />

              <Select
                size="sm"
                width="7rem"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as 'az' | 'za')}
                bg="white"
                borderColor="gray.200"
                aria-label="Sort states by name"
              >
                <option value="az">A - Z</option>
                <option value="za">Z - A</option>
              </Select>
            </Flex>
          </Flex>

          <FilterStateIndicator
            appliedFilters={filters}
            totalCount={0}
            filteredCount={0}
            resourceType="plans"
            onClearFilters={handleClearFilters}
            marketOptions={[]}
            stateOptions={stateOptions}
          />

          {statesStale && (
            <Flex
              align="center"
              justify="space-between"
              bg="orange.50"
              border="1px solid"
              borderColor="orange.200"
              borderRadius="0.375rem"
              px="1rem"
              py="0.625rem"
              mt="0.75rem"
            >
              <Text fontSize="0.8125rem" color="orange.700">
                Showing previously loaded states - the latest refresh
                failed, so market names or counts below may be out of date.
              </Text>
              <Button
                size="sm"
                colorScheme="orange"
                variant="outline"
                onClick={loadStates}
              >
                Retry
              </Button>
            </Flex>
          )}

          {catalogError ? (
            <Flex
              align="center"
              justify="space-between"
              bg="red.50"
              border="1px solid"
              borderColor="red.200"
              borderRadius="0.375rem"
              px="1rem"
              py="0.625rem"
              mt="0.75rem"
            >
              <Text fontSize="0.8125rem" color="red.700">
                Could not load the plan catalog. "Manage Plans" counts and
                the add-plan search are disabled until this loads.
              </Text>
              <Button
                size="sm"
                colorScheme="red"
                variant="outline"
                onClick={loadAllInsurance}
              >
                Retry
              </Button>
            </Flex>
          ) : catalogLoading ? (
            <Text fontSize="0.8125rem" color="gray.500" mt="0.75rem">
              Loading plan catalog…
            </Text>
          ) : null}

          <Box mt="1rem">
            {statesLoading ? (
              <PageLoader height="45vh" />
            ) : statesLoadError && statesData.length === 0 ? (
              <Flex
                direction="column"
                align="center"
                gap="0.75rem"
                py="2rem"
                textAlign="center"
              >
                <Text color="red.600" fontSize="0.9375rem" fontWeight="600">
                  Could not load states and markets.
                </Text>
                <Text color="gray.500" fontSize="0.8125rem">
                  This is a loading problem, not an empty account - try
                  again.
                </Text>
                <Button
                  size="sm"
                  colorScheme="red"
                  variant="outline"
                  onClick={loadStates}
                >
                  Retry
                </Button>
              </Flex>
            ) : sortedStates.length === 0 ? (
              <Text color="gray.500" py="2rem" textAlign="center">
                No states with markets available to display.
              </Text>
            ) : (
              <VStack spacing="0.875rem" align="stretch">
                {sortedStates.map((state) => {
                  const abbreviation =
                    stateAbbreviationByName.get(state.name.toLowerCase()) ||
                    state.name;
                  return (
                    <StatePlansSection
                      key={`${customerId ?? ''}-${state.name}`}
                      stateName={state.name}
                      stateAbbreviation={abbreviation}
                      practiceCount={state.practiceCount}
                      providerCount={state.providerCount}
                      isExpanded={expandedStates.has(state.name)}
                      onToggleExpand={() => toggleExpand(state.name)}
                      isManageOpen={expandedManagePlans.has(state.name)}
                      onToggleManage={() => toggleManage(state.name)}
                      assignedPlans={insuranceByState.get(abbreviation) || []}
                      allPlans={allInsurance}
                      globalFilters={globalFiltersForSections}
                      enhancedBadges={enhancedBadges}
                      onPlansChanged={loadAllInsurance}
                      canManagePlans={canEditInsurance() || isAdmin()}
                      catalogReady={!catalogLoading && !catalogError}
                      onSectionLoadSettled={() =>
                        handleSectionLoadSettled(state.name)
                      }
                    />
                  );
                })}
              </VStack>
            )}
          </Box>
        </Box>
      </MotionBox>

      <Modal
        isOpen={isLegendOpen}
        onClose={() => setIsLegendOpen(false)}
        size="xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Status Badge Legend & Help</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb="2rem">
            <Stack spacing="1.5rem">
              <Box>
                <Text fontSize="1.125rem" fontWeight="600" mb="0.5rem">
                  Enhanced Badge Mode: {enhancedBadges ? 'ON' : 'OFF'}
                </Text>
                <Text color="gray.600" fontSize="0.875rem">
                  {enhancedBadges
                    ? 'Badges show two lines: Title (bold) + Subtitle (smaller) for better readability.'
                    : 'Badges show single line with basic status information.'}
                </Text>
              </Box>

              <Box>
                <Text fontSize="1.125rem" fontWeight="600" mb="1rem">
                  Status Color Meanings
                </Text>
                <Stack spacing="1rem">
                  {[
                    {
                      color: 'green.500',
                      title: 'OK to Schedule',
                      subtitle: 'Assigned HMO PCP must be in the tax ID',
                      description:
                        'This provider is fully credentialed and can accept new patients for this insurance plan.',
                      example: 'Schedule Allowed',
                    },
                    {
                      color: 'teal.500',
                      title: 'New patients MUST be 10+ days out',
                      subtitle: 'Provider credentialed and active',
                      description:
                        'VA insurance plan where the provider is credentialed. New patients must be scheduled at least 10 days in advance.',
                      example: 'VA — Schedule Allowed',
                    },
                    {
                      color: 'purple.500',
                      title: 'HMO plan requires exact PCP match',
                      subtitle: 'Exact PCP match required for HMO plans',
                      description:
                        "HMO plans require the provider to be the exact PCP listed in the patient's plan.",
                      example: 'PCP Match',
                    },
                    {
                      color: 'blue.500',
                      title: 'Schedule Established patients',
                      subtitle: 'Panel is closed; No new patients',
                      description:
                        'Provider can only see existing patients. New patient panel is closed.',
                      example: 'Established patients only',
                    },
                    {
                      color: 'yellow.500',
                      title: 'Do not schedule',
                      subtitle: 'Application/credentialing is in process',
                      description:
                        "Provider's application is being processed. Wait for completion before scheduling.",
                      example: 'Pending Credentialing',
                    },
                    {
                      color: 'red.500',
                      title: 'Do not schedule',
                      subtitle: 'Provider does not take this plan',
                      description:
                        'Provider is not contracted with this insurance plan. Do not schedule patients.',
                      example: 'Not Participating',
                    },
                    {
                      color: 'orange.500',
                      title: 'Status Unknown',
                      subtitle: 'Contact administrator for clarification',
                      description:
                        'Status information is unclear, or nothing has been recorded for this provider yet.',
                      example: 'No Status',
                    },
                  ].map((item, index) => (
                    <Flex key={index} gap="1rem" alignItems="flex-start">
                      <Box
                        w="2rem"
                        h="2rem"
                        bg={item.color}
                        borderRadius="0.375rem"
                        flexShrink={0}
                      />
                      <Box flex="1">
                        <Text fontWeight="600" color={item.color}>
                          {item.title}
                        </Text>
                        <Text fontSize="0.875rem" color="gray.600" mb="0.25rem">
                          {item.subtitle}
                        </Text>
                        <Text fontSize="0.875rem" color="gray.700" mb="0.25rem">
                          {item.description}
                        </Text>
                        <Text
                          fontSize="0.75rem"
                          color="gray.500"
                          fontStyle="italic"
                        >
                          Example: {item.example}
                        </Text>
                      </Box>
                    </Flex>
                  ))}
                </Stack>
              </Box>

              <Box>
                <Text fontSize="1.125rem" fontWeight="600" mb="0.5rem">
                  How to Use
                </Text>
                <Text fontSize="0.875rem" color="gray.600">
                  • Hover over any status badge to see full details
                  <br />
                  • Use the toggle above to switch between enhanced and basic
                  badge modes
                  <br />• Contact administrator if you see "Status Unknown" or
                  unclear statuses
                </Text>
              </Box>
            </Stack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default PlansList;
